import { Star } from 'lucide-react';

import { cn } from '@/lib/utils';

// Shared read-only star row (doc 60 §4.6): every surface that renders a rating
// -- discover reviews tab, menu-page section, merchant inbox -- draws from this
// one component so the star shape can never drift between them.

export function ReviewStars({ value, className }: { value: number; className?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-0.5', className)} aria-hidden>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={cn(
            'h-3.5 w-3.5',
            i <= Math.round(value)
              ? 'fill-[var(--color-warning)] text-[var(--color-warning)]'
              : 'text-[var(--color-text-secondary)]/35',
          )}
        />
      ))}
    </span>
  );
}

// Histogram for the rating summary. Bars have no filled background track: a
// full-width grey track behind every row is dashboard clutter and makes an
// empty star look like a full one at a glance. Keyed 1-5 and always fully
// populated, so a star nobody has given renders as an empty bar rather than a
// missing row.
export function ReviewHistogram({
  breakdown,
  total,
  className,
}: {
  breakdown: Record<string, number>;
  total: number;
  className?: string;
}) {
  const stars = [5, 4, 3, 2, 1] as const;
  return (
    <ul className={cn('space-y-1.5', className)} aria-label="Rating breakdown">
      {stars.map((star) => {
        const count = Number(breakdown?.[String(star)] ?? 0);
        const pct = total > 0 ? (count / total) * 100 : 0;
        return (
          <li key={star} className="flex items-center gap-2 text-xs">
            <span className="w-3 shrink-0 tabular-nums text-[var(--color-text-secondary)]">{star}</span>
            <span className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-[var(--color-bg-sunken)]">
              <span
                className="block h-full rounded-full bg-[var(--color-warning)]"
                style={{ width: `${pct}%` }}
              />
            </span>
            <span className="w-6 shrink-0 text-right tabular-nums text-[var(--color-text-secondary)]">{count}</span>
          </li>
        );
      })}
    </ul>
  );
}
